<script setup lang="ts">
// eslint-disable-next-line import/no-extraneous-dependencies
import type { SearchParameters } from 'algoliasearch-helper';
import { connectHitsWithInsights } from 'instantsearch.js/es/connectors';
import {
  HitsConnectorParams,
  HitsWidgetDescription,
} from 'instantsearch.js/es/connectors/hits/connectHits';
import { computed, PropType } from 'vue';
// @ts-expect-error (function lacks types; this should be converted in the near future)
import { createSuitMixin } from 'vue-instantsearch/vue3/es';

import { AlgoliaVariant } from '@/api/algolia';
import {
  MerchandisingBanner,
  MerchandisingBannerSyntheticHit,
} from '@/components/search/products/BannerCard.vue';
import { useWidget } from '@/composables/instant-search/useWidget';
import { useFeatureFlags } from '@/composables/useFeatureFlags';

defineOptions({
  mixins: [createSuitMixin({ name: 'Hits' })],
});

const { flags } = useFeatureFlags();

const props = defineProps({
  escapeHTML: { type: Boolean, default: true },
  initialRefinements: {
    type: Object as PropType<{
      disjunctiveFacetsRefinements: SearchParameters['disjunctiveFacetsRefinements'];
      facetsRefinements: SearchParameters['facetsRefinements'];
      hierarchicalFacetsRefinements: SearchParameters['hierarchicalFacetsRefinements'];
    }>,
    default: () => ({
      disjunctiveFacetsRefinements: {},
      facetsRefinements: {},
      hierarchicalFacetsRefinements: {},
    }),
  },
  showVariants: { type: Boolean },
  transformItems: {
    required: false,
    type: Function as PropType<HitsConnectorParams['transformItems']>,
  },
});

const widgetParams = computed<HitsConnectorParams>(() => ({
  escapeHTML: props.escapeHTML,
  transformItems: props.transformItems,
}));

const { state } = useWidget<HitsWidgetDescription, HitsConnectorParams>(
  { connector: connectHitsWithInsights },
  widgetParams,
);

const activeRefinements = computed(() => {
  const { state: helperState } = state.value?.instantSearchInstance.helper ?? {};
  return {
    disjunctiveFacetsRefinements: helperState?.disjunctiveFacetsRefinements ?? {},
    facetsRefinements: helperState?.facetsRefinements ?? {},
    hierarchicalFacetsRefinements: helperState?.hierarchicalFacetsRefinements ?? {},
  };
});
const exceedsPreFilteringParams = computed<boolean>(() =>
  Object.entries(activeRefinements.value).some(([facetType, facetGroup]) =>
    Object.entries(facetGroup).some(([groupKey, facets]) => {
      if (!facets.length) return false;
      const initialFacetGroup =
        props.initialRefinements[facetType as keyof typeof props.initialRefinements];
      if (!initialFacetGroup?.[groupKey]) return true;
      return facets.some((facet) => !initialFacetGroup[groupKey]?.includes(facet));
    }),
  ),
);

const customData = computed<MerchandisingBanner[]>(() => state.value?.results?.userData ?? []);
const canDisplayBanners = computed<boolean>(() => {
  const { nbHits, page, query } = state.value?.results ?? {};
  if (!flags.algoliaCustomDataBanners) return false;

  return !exceedsPreFilteringParams.value && page === 0 && !!nbHits && !query;
});
const items = computed(() => {
  const results: (AlgoliaVariant | MerchandisingBannerSyntheticHit)[] = [
    ...((state.value?.hits as AlgoliaVariant[]) ?? []),
  ];
  if (!canDisplayBanners.value) return results;

  customData.value.forEach((bannerCtx) => {
    if (!bannerCtx.banner || !('position' in bannerCtx)) return;
    let { position } = bannerCtx;
    if (position > 0) position -= 1; // assume users start with 1 instead of 0
    results.splice(position, 0, {
      ...bannerCtx,
      objectID: bannerCtx.title,
      position,
      __position: position,
    });
  });
  return results;
});
</script>

<template>
  <div v-if="state" :class="suit()">
    <slot :items="items" :insights="state.insights">
      <ol :class="suit('list')">
        <li v-for="(item, itemIndex) in items" :key="item.objectID" :class="suit('item')">
          <slot name="item" :item="item" :index="itemIndex" :insights="state.insights"
            >objectID: {{ item.objectID }}, index: {{ itemIndex }}</slot
          >
        </li>
      </ol>
    </slot>
  </div>
</template>
